import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  addComplianceRequirement,
  removeComplianceRequirement,
  setAdditionalRequirements,
  setDeliveryLocation,
  setLeadTime,
  setSourcingLocation,
  setSupplierPriority,
} from '../store/rfqSlice';
import { voiceAppCommandBus } from '../services/VoiceAppCommandBus';
import { trpClient } from '../client/trpClient';
import { useRFQ } from '../contexts/RFQContext';
import {
  SOURCING_PRIORITY_OPTIONS,
  SourcingPriorityOption,
  prioritiesFromBackend,
} from '../utils/sourcingCriteria';
import { useDiscoveryExit } from './useDiscoveryExit';
import { useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { robbieActionText } from '../utils/robbieActivity';
import { DiscoveryStarted } from '../types/discovery.types';

/**
 * Wizard Step 2 logic — sourcing criteria state mirrored with the store,
 * voice-command registration, and the save-then-start-discovery submit (the
 * exit that charges the BOM lives in useDiscoveryExit). The saved
 * shape (tradeoffPriorities ids, compliance values, lead-time preset,
 * deliveryLocation.formattedAddress, notes) is what the research pipeline has
 * always consumed. Both locations are plain text and optional: an empty string
 * is saved rather than omitted, so clearing a field actually clears it
 * downstream instead of leaving the previous answer in place.
 */
const NO_IDS: string[] = [];

export const useCriteriaStep = (
  rfq: any,
  onNext: () => void,
  /** Hears what the 202 charged, before the step moves on (the landing toast). */
  onDiscoveryStarted?: (result: DiscoveryStarted) => void,
) => {
  const dispatch = useDispatch();
  const { fetchRFQ } = useRFQ();
  const recordAction = useRecordRobbieAction();
  const { commercialTerms } = useSelector((state: RootState) => state.rfq);

  const [compliance, setComplianceState] = useState<string[]>(commercialTerms.complianceRequirements);
  const [leadTime, setLeadTimeState] = useState(commercialTerms.desiredLeadTime);
  const [location, setLocationState] = useState(commercialTerms.deliveryLocation);
  const [sourcingLocation, setSourcingLocationState] = useState(commercialTerms.sourcingLocation);
  const [notes, setNotesState] = useState(commercialTerms.additionalRequirements);
  const [priorities, setPriorities] = useState<SourcingPriorityOption[]>(() => [...SOURCING_PRIORITY_OPTIONS]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const onDiscoveryStartedRef = useRef(onDiscoveryStarted);
  onDiscoveryStartedRef.current = onDiscoveryStarted;

  // Leaving step 2 charges the BOM; a short balance opens the picker instead.
  // The refetch is silent: the StepBar's busy button is the only wait shown.
  const started = useCallback(async (result?: DiscoveryStarted) => {
    await fetchRFQ(rfq.rfqId, true, true);
    if (result) onDiscoveryStartedRef.current?.(result);
    voiceAppCommandBus.sendVoiceFeedback('step2Complete', {
      complianceCount: commercialTerms.complianceRequirements.length,
      hasLeadTime: !!commercialTerms.desiredLeadTime,
      message: 'Requirements saved. Analysis started...',
    });
    onNext();
  }, [rfq.rfqId, fetchRFQ, commercialTerms, onNext]);
  const exit = useDiscoveryExit({ rfqId: rfq.rfqId, onStarted: started });
  const beginDiscovery = exit.begin;

  const extractedData = useSelector(
    (state: RootState) => (state.rfq.rfqData as any)?.analysisData || (state.rfq.rfqData as any)?.extractedData
  );
  const components: any[] = extractedData?.components || [];
  // Lines discovery is already paid for; an older backend omits the field.
  const paidComponentIds: string[] = useMemo(() => extractedData?.paidComponentIds ?? NO_IDS, [extractedData]);
  const fileName: string | undefined = extractedData?.metadata?.fileNames?.[0];

  // Local state mirrors the store so voice/tool updates land on screen.
  useEffect(() => setLeadTimeState(commercialTerms.desiredLeadTime), [commercialTerms.desiredLeadTime]);
  useEffect(() => setComplianceState(commercialTerms.complianceRequirements), [commercialTerms.complianceRequirements]);
  useEffect(() => setNotesState(commercialTerms.additionalRequirements), [commercialTerms.additionalRequirements]);
  useEffect(() => setLocationState(commercialTerms.deliveryLocation), [commercialTerms.deliveryLocation]);
  useEffect(() => setSourcingLocationState(commercialTerms.sourcingLocation), [commercialTerms.sourcingLocation]);

  // Saved rankings may be backend ids or full option objects — both coerce.
  useEffect(() => {
    if (!commercialTerms.supplierPriority) return;
    try {
      const saved = JSON.parse(commercialTerms.supplierPriority);
      if (Array.isArray(saved) && saved.length > 0) setPriorities(prioritiesFromBackend(saved));
    } catch {
      // Unparseable saved ranking — keep the current order.
    }
  }, [commercialTerms.supplierPriority]);

  const reorderPriorities = useCallback(
    (next: SourcingPriorityOption[]) => {
      setPriorities(next);
      dispatch(setSupplierPriority(JSON.stringify(next)));
      voiceAppCommandBus.sendVoiceFeedback('priorityRanking', {
        ranking: next.map((item, idx) => ({ ...item, rank: idx + 1 })),
      });
    },
    [dispatch]
  );

  const toggleCompliance = useCallback(
    (value: string) => {
      const next = compliance.includes(value)
        ? compliance.filter((c) => c !== value)
        : [...compliance, value];
      setComplianceState(next);
      dispatch(next.includes(value) ? addComplianceRequirement(value) : removeComplianceRequirement(value));
      voiceAppCommandBus.sendVoiceFeedback('complianceToggled', {
        complianceValue: value,
        action: next.includes(value) ? 'added' : 'removed',
        totalCount: next.length,
      });
    },
    [compliance, dispatch]
  );

  const changeLeadTime = useCallback(
    (value: string) => {
      setLeadTimeState(value);
      dispatch(setLeadTime(value));
      voiceAppCommandBus.sendVoiceFeedback('leadTimeChanged', { leadTime: value });
    },
    [dispatch]
  );

  const changeLocation = useCallback(
    (value: string) => {
      setLocationState(value);
      dispatch(setDeliveryLocation(value));
    },
    [dispatch]
  );

  const changeSourcingLocation = useCallback(
    (value: string) => {
      setSourcingLocationState(value);
      dispatch(setSourcingLocation(value));
    },
    [dispatch]
  );

  const changeNotes = useCallback(
    (value: string) => {
      setNotesState(value);
      dispatch(setAdditionalRequirements(value));
    },
    [dispatch]
  );

  const submit = useCallback(async () => {
    setAnalysisError(null);
    if (!leadTime) return;
    setIsAnalyzing(true);
    try {
      const requirements = {
        tradeoffPriorities: priorities.map((item) => item.id),
        complianceRequirements: compliance,
        desiredLeadTime: leadTime,
        // Optional like sourcingLocation: '' means "not specified" downstream.
        deliveryLocation: { formattedAddress: location.trim() },
        // Optional: send '' rather than omitting, so clearing it clears the
        // saved value instead of leaving the old one in place.
        sourcingLocation: sourcingLocation.trim(),
        notes,
      };
      // Saved first, so a refusal at the exit (which writes nothing) still
      // keeps what the buyer chose.
      await trpClient.updateRequirements(rfq.rfqId, requirements);
      recordAction('saveCriteria', robbieActionText.savedCriteria(leadTime, compliance.length));
      await beginDiscovery(requirements);
    } catch (error: any) {
      const message = error.message || 'Failed to save requirements';
      setAnalysisError(message);
      voiceAppCommandBus.sendVoiceFeedback('analysisError', { error: message });
    } finally {
      setIsAnalyzing(false);
    }
  }, [rfq, priorities, compliance, leadTime, location, sourcingLocation, notes, beginDiscovery, recordAction]);

  // Voice commands — same command set the bus has always exposed on step 2.
  useEffect(() => {
    voiceAppCommandBus.registerAppCommand('addCompliance', async (params: any) => {
      const id = params?.complianceId;
      if (id && !compliance.includes(id)) {
        toggleCompliance(id);
        return { success: true, message: `Added ${id} requirement` };
      }
      return { success: false, message: 'Invalid or duplicate compliance requirement' };
    });
    voiceAppCommandBus.registerAppCommand('removeCompliance', async (params: any) => {
      const id = params?.complianceId;
      if (id && compliance.includes(id)) {
        toggleCompliance(id);
        return { success: true, message: `Removed ${id} requirement` };
      }
      return { success: false, message: 'Compliance requirement not found' };
    });
    voiceAppCommandBus.registerAppCommand('setLeadTime', async (params: any) => {
      if (params?.leadTime) {
        changeLeadTime(params.leadTime);
        return { success: true, message: `Lead time set to ${params.leadTime}` };
      }
      return { success: false, message: 'Invalid lead time' };
    });
    voiceAppCommandBus.registerAppCommand('getRequirements', async () => ({
      success: true,
      data: { compliance, leadTime, additionalRequirements: notes },
    }));
    voiceAppCommandBus.registerAppCommand('setPriorityRanking', async (params: any) => {
      if (Array.isArray(params?.ranking)) {
        reorderPriorities(params.ranking);
        return { success: true, message: 'Priority ranking updated successfully' };
      }
      return { success: false, message: 'Invalid priority ranking' };
    });
    voiceAppCommandBus.registerAppCommand('analyzeBOM', async () => {
      if (!leadTime) {
        return { success: false, message: 'Please fill in the required lead time field before triggering analysis' };
      }
      submit();
      return { success: true, message: 'BOM analysis triggered successfully' };
    });

    voiceAppCommandBus.updateContext('currentStep', 2);
    voiceAppCommandBus.updateContext('requirements', { compliance, leadTime, priorityRanking: priorities });

    return () => {
      voiceAppCommandBus.unregisterAppCommand('addCompliance');
      voiceAppCommandBus.unregisterAppCommand('removeCompliance');
      voiceAppCommandBus.unregisterAppCommand('setLeadTime');
      voiceAppCommandBus.unregisterAppCommand('getRequirements');
      voiceAppCommandBus.unregisterAppCommand('setPriorityRanking');
      voiceAppCommandBus.unregisterAppCommand('analyzeBOM');
    };
  }, [compliance, leadTime, notes, priorities, toggleCompliance, changeLeadTime, reorderPriorities, submit]);

  return {
    components,
    paidComponentIds,
    fileName,
    priorities,
    reorderPriorities,
    compliance,
    toggleCompliance,
    leadTime,
    changeLeadTime,
    location,
    changeLocation,
    sourcingLocation,
    changeSourcingLocation,
    notes,
    changeNotes,
    isAnalyzing: isAnalyzing || exit.starting,
    analysisError: analysisError ?? exit.error,
    dismissAnalysisError: () => {
      setAnalysisError(null);
      exit.dismissError();
    },
    exit,
    isValid: Boolean(leadTime),
    submit,
  };
};

export default useCriteriaStep;
